/**
 * assets/customizer-base.js
 * Base class and core utilities for AI Image customizers.
 */
class BaseCustomizer {
  constructor(config) {
    this.productId = config.productId;
    this.aiImagePath = config.aiImagePath;
    this.userId = config.userId;
    this.currentSessionId = '';
    this.isGenerating = false;
    
    window.allCustomizations = window.allCustomizations || [];
    window.productGroupColors = window.productGroupColors || {};
    window.selectedCustomizationIndex = (typeof window.selectedCustomizationIndex !== 'undefined') ? window.selectedCustomizationIndex : -1;
    
    this.loadingMessages = [
      "Turning your photo into art... 🎨",
      "Sprinkling some magic... ✨",
      "Blending colors and brushstrokes... 🖌️",
      "Hang tight! Good art takes a moment... ⏳",
      "Polishing every little detail... ✂️",
      "Almost ready to reveal! 🎉",
      "Get ready to smile! 😄"
    ];
    
    this.bindGlobalMethods();
  }

  get allCustomizations() {
    return window.allCustomizations;
  }
  set allCustomizations(val) {
    window.allCustomizations = val;
  }
  
  get selectedCustomizationIndex() {
    return window.selectedCustomizationIndex;
  }
  set selectedCustomizationIndex(val) {
    window.selectedCustomizationIndex = val;
  }

  get productGroupColors() {
    return window.productGroupColors;
  }
  set productGroupColors(val) {
    window.productGroupColors = val;
  }

  bindGlobalMethods() {
    
    window.openModal = () => this.openModal();
    window.closeModal = (e) => this.closeModal(e);
    window.closeModalBtn = () => this.closeModalBtn();
    
    window.openGalleryModal = () => this.openGalleryModal();
    window.closeGalleryModal = () => this.closeGalleryModal();
    window.openImageZoom = (url) => this.openImageZoom(url);
    window.closeImageZoom = () => this.closeImageZoom();
    
    window.closeLoginModal = (e) => this.closeLoginModal(e);
    window.closeLoginModalBtn = () => this.closeLoginModalBtn();
    window.redirectToLogin = () => this.redirectToLogin();
    window.continueAsGuest = () => this.continueAsGuest();
    
    window.lockScreen = () => this.lockScreen();
    window.unlockScreen = () => this.unlockScreen();
    window.getAiAuthTokens = () => this.getAiAuthTokens();
    window.showCustomAlert = (msg, title, showCancel, callback) => this.showCustomAlert(msg, title, showCancel, callback);
    window.updateColorVariants = () => this.updateColorVariants();
    
    window.fetchUserGallery = () => this.fetchUserGallery();
    window.updateRemoteHistoryItem = (key, updates) => this.updateRemoteHistoryItem(key, updates);
  }

  lockScreen() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add('modal-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  unlockScreen() {
    document.body.classList.remove('modal-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  async getAiAuthTokens() {
    const authTokens = document.getElementById('auth-tokens');
    if (!authTokens) {
      return { userId: '-1', timestamp: '', signature: '' };
    }
    return {
      userId: authTokens.getAttribute('data-uid'),
      timestamp: authTokens.getAttribute('data-ts'),
      signature: authTokens.getAttribute('data-sig')
    };
  }

  showCustomAlert(message, title = '', showCancel = false, callback = null) {
    const alertModal = document.getElementById('custom-alert-modal');
    if (!alertModal) {
      alert(message);
      if (callback) callback(true);
      return;
    }
    
    const titleEl = document.getElementById('custom-alert-title');
    const msgEl = document.getElementById('custom-alert-message');
    const okBtn = document.getElementById('custom-alert-ok-btn');
    const cancelBtn = document.getElementById('custom-alert-cancel-btn');

    if (title) {
      titleEl.innerText = title;
      titleEl.style.display = 'block';
    } else {
      titleEl.style.display = 'none';
    }

    msgEl.innerText = message;
    cancelBtn.style.display = showCancel ? 'inline-block' : 'none';
    alertModal.style.display = 'flex';
    this.lockScreen();

    const cleanup = (confirmed) => {
      alertModal.style.display = 'none';
      this.unlockScreen();
      okBtn.replaceWith(okBtn.cloneNode(true));
      cancelBtn.replaceWith(cancelBtn.cloneNode(true));
      if (callback) callback(confirmed);
    };

    document.getElementById('custom-alert-ok-btn').addEventListener('click', () => cleanup(true));
    document.getElementById('custom-alert-cancel-btn').addEventListener('click', () => cleanup(false));
  }

  openModal() {
    const modal = document.getElementById('custom-product-modal');
    if (modal) {
      modal.style.display = 'flex';
      this.lockScreen();
    }
  }

  closeModal(event) {
    if (event.target.id === 'custom-product-modal') {
      this.closeModalBtn();
    }
  }

  closeModalBtn() {
    const modal = document.getElementById('custom-product-modal');
    if (modal) {
      modal.style.display = 'none';
      this.unlockScreen();
    }
  }

  openGalleryModal() {
    const modal = document.getElementById('ai-gallery-modal');
    if (modal) {
      modal.style.display = 'flex';
      this.lockScreen();
    }
  }

  closeGalleryModal() {
    const modal = document.getElementById('ai-gallery-modal');
    if (modal) {
      modal.style.display = 'none';
      this.unlockScreen();
    }
  }

  openImageZoom(url) {
    const modal = document.getElementById('image-zoom-modal');
    const img = document.getElementById('zoomed-image-display');
    if (modal && img) {
      img.src = url;
      modal.style.display = 'flex';
      this.lockScreen();
    }
  }

  closeImageZoom() {
    const modal = document.getElementById('image-zoom-modal');
    if (modal) {
      modal.style.display = 'none';
      this.unlockScreen();
    }
  }

  closeLoginModal(event) {
    if (event.target.id === 'login-confirm-modal') {
      this.closeLoginModalBtn();
    }
  }

  closeLoginModalBtn() {
    const modal = document.getElementById('login-confirm-modal');
    if (modal) {
      modal.style.display = 'none';
      this.unlockScreen();
    }
  }

  redirectToLogin() {
    window.location.href = `/account/login?checkout_url=${encodeURIComponent(window.location.href)}`;
  }

  continueAsGuest() {
    this.closeLoginModalBtn();
    this.openModal();
  }

  async fetchUserGallery() {
    let attempts = 0;
    const maxAttempts = 2;
    while (attempts < maxAttempts) {
      try {
        attempts++;
        const tokens = await this.getAiAuthTokens();
        if (tokens.userId === '-1') return;

        const workerUrl = "https://server.museminor.com/imagehistory";
        const targetUrl = `${workerUrl}?productId=${this.productId}`;
        const response = await fetch(targetUrl, {
          headers: {
            'X-USER-ID': tokens.userId,
            'X-USER-Timestamp': tokens.timestamp,
            'X-USER-Signature': tokens.signature
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.images && Array.isArray(data.images)) {
            this.allCustomizations = data.images.map(img => {
              var obj = { imageUrl: img.imageUrl, key: img.key, variantId: img.variantId || null };
              if (['namelabel', 'bookmark', 'clothes', 'dtf-sheet', 'tote', 'small-tote', 'photo-frame'].includes(this.aiImagePath)) {
                obj.name = img.name || '';
              } else if (['greetingCard', 'fridge-magnet'].includes(this.aiImagePath)) {
                obj.customTitle = img.customTitle || '';
                obj.customMessage = img.customMessage || '';
              }
              return obj;
            });
            window.allCustomizations = this.allCustomizations;
            this.productGroupColors = {};
            window.productGroupColors = this.productGroupColors;
            if (window.renderCustomizationsList) window.renderCustomizationsList();
          }
          break;
        } else {
          throw new Error(`Status ${response.status}`);
        }
      } catch (e) {
        console.warn(`Gallery fetch attempt ${attempts} failed`, e);
        if (attempts < maxAttempts) await new Promise(r => setTimeout(r, 1000));
      }
    }
  }

  async updateRemoteHistoryItem(key, updates) {
    try {
      const tokens = await this.getAiAuthTokens();
      if (tokens.userId === '-1') return;

      const workerUrl = "https://server.museminor.com/imagehistory";
      const targetUrl = `${workerUrl}?productId=${this.productId}&key=${key}`;

      const response = await fetch(targetUrl, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'X-USER-ID': tokens.userId,
          'X-USER-Timestamp': tokens.timestamp,
          'X-USER-Signature': tokens.signature
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        console.error("Failed to update remote history item", await response.text());
      }
    } catch (e) {
      console.error("Error updating remote history item", e);
    }
  }
}

/**
 * Shared intermediate class for color products (Tote & Clothes)
 */
class ColorCustomizer extends BaseCustomizer {
  constructor(config) {
    super(config);
    this.colorDetails = config.colorDetails;
    this.currentModalGroupKey = null;
    this.modalSelectedIdx = 0;
  }

  bindGlobalMethods() {
    super.bindGlobalMethods();
    window.openChangeColorModal = (gKey) => this.openChangeColorModal(gKey);
    window.closeColorModal = (e) => this.closeColorModal(e);
    window.closeColorModalBtn = () => this.closeColorModalBtn();
    window.renderColorSwatches = (containerId, currentColor, callbackName, extraParam) => 
      this.renderColorSwatches(containerId, currentColor, callbackName, extraParam);
  }

  openChangeColorModal(gKey) {
    const modal = document.getElementById('clothes-color-modal');
    if (!modal) return;
    
    this.modalSelectedIdx = 0;
    this.currentModalGroupKey = gKey;
    
    window.refreshModalPreviews = () => {
      const groupItems = this.allCustomizations.filter(item => 
        item.key && (item.key === this.currentModalGroupKey || item.key.startsWith(this.currentModalGroupKey + '-'))
      );
      
      if (groupItems.length === 0) return;

      const mainContainer = document.getElementById('modal-color-preview-container');
      const thumbContainer = document.getElementById('modal-color-thumbnails-strip');
      
      window.selectModalColorPreview = (idx) => {
        this.modalSelectedIdx = idx;
        const activeItem = groupItems[idx];
        if (!activeItem) return;
        
        const currentColor = this.productGroupColors[this.currentModalGroupKey] || (this.aiImagePath === 'clothes' ? 'pink' : 'white');
        
        let previewUrl = activeItem.imageUrl;
        let itemVariantId = activeItem.variantId;
        
        if (itemVariantId) {
          const originalVar = window.productVariants.find(v => String(v.id) === String(itemVariantId));
          if (originalVar) {
            const parsed = window.parseVariantTitle(originalVar.title);
            const matchVar = window.productVariants.find(v => {
              const p = window.parseVariantTitle(v.title);
              if (p.style !== parsed.style) return false;
              return p.color.toLowerCase() === currentColor.toLowerCase() && p.size === parsed.size;
            }) || window.productVariants.find(v => {
              const p = window.parseVariantTitle(v.title);
              if (p.style !== parsed.style) return false;
              return p.color.toLowerCase() === currentColor.toLowerCase();
            }) || originalVar;
            
            previewUrl = window.getUpdatedProductUrl(previewUrl, matchVar.id);
          }
        }
        
        if (mainContainer) {
          mainContainer.innerHTML = `<img src="${previewUrl}" alt="Main Color Preview" style="width: 100%; display: block; object-fit: contain;">`;
        }
        
        const thumbImgs = thumbContainer.querySelectorAll('img');
        thumbImgs.forEach((img, i) => {
          if (i === idx) img.style.borderColor = '#6e8efb';
          else img.style.borderColor = '#eee';
        });
      };

      if (groupItems.length > 1) {
        thumbContainer.style.display = 'flex';
        thumbContainer.innerHTML = groupItems.map((item, idx) => {
          const isActive = idx === this.modalSelectedIdx;
          return `
            <div class="thumb-wrapper" onclick="window.selectModalColorPreview(${idx})" style="flex-shrink: 0; width: 50px; height: 50px; border-radius: 4px; border: 2px solid ${isActive ? '#6e8efb' : '#eee'}; overflow: hidden; cursor: pointer; transition: all 0.2s;">
              <img src="${item.imageUrl}" style="width: 100%; height: 100%; object-fit: cover;">
            </div>
          `;
        }).join('');
      } else {
        thumbContainer.style.display = 'none';
      }

      window.selectModalColorPreview(this.modalSelectedIdx);
    };

    window.refreshModalPreviews();

    const currentColor = this.productGroupColors[gKey] || (this.aiImagePath === 'clothes' ? 'pink' : 'white');
    this.renderColorSwatches('color-options-container', currentColor, 'updateProductColor', gKey);
    
    modal.style.display = 'flex';
    this.lockScreen();
  }

  closeColorModalBtn() {
    const modal = document.getElementById('clothes-color-modal');
    if (modal) modal.style.display = 'none';
    this.unlockScreen();
    this.currentModalGroupKey = null;
    if (window.scrollToSelectedCustomization) window.scrollToSelectedCustomization();
  }

  closeColorModal(event) {
    if (event.target.id === 'clothes-color-modal') {
      // Prevent accidental closing
    }
  }

  renderColorSwatches(containerId, currentColor, callbackName, extraParam) {
    const container = document.getElementById(containerId);
    if (!container) return;
    
    const colors = window.getProductColors();
    container.innerHTML = colors.map(color => {
      const isActive = currentColor.toLowerCase() === color.value.toLowerCase();
      const borderColor = color.value === 'white' ? '#ddd' : color.hex;
      return `
        <div class="color-option-large ${isActive ? 'active' : ''}" 
             onclick="window.${callbackName}('${color.value}'${extraParam ? `, '${extraParam}'` : ''})"
             style="display: flex; flex-direction: column; align-items: center; gap: 4px; cursor: pointer; transition: transform 0.2s; min-width: 45px; width: max-content; text-align: center;">
          <div style="width: 24px; height: 24px; border-radius: 50%; background: ${color.hex}; border: 1px solid ${borderColor}; position: relative; ${isActive ? 'box-shadow: 0 0 0 2px #fff, 0 0 0 3px #6e8efb;' : ''}">
            ${isActive ? '<div style="position: absolute; top: -2px; right: -2px; background: #6e8efb; color: white; border-radius: 50%; width: 12px; height: 12px; display: flex; align-items: center; justify-content: center; font-size: 7px; border: 1px solid #fff;">✓</div>' : ''}
          </div>
          <span style="font-size: 0.75rem; font-weight: ${isActive ? '600' : '500'}; color: ${isActive ? '#6e8efb' : '#4b5563'}; white-space: nowrap;">${color.name}</span>
        </div>
      `;
    }).join('');
  }
}
